/**
 * The one HTTP client. Nothing else in the app calls fetch() (FRONTEND_PLAN rule 3).
 *
 * Responsibilities: URL building, bearer injection, `credentials: 'include'`, `Idempotency-Key`,
 * envelope unwrapping, `ApiError` construction (incl. `request_id`), the 401 → single-flight
 * refresh → retry-once path, and the retry policy for 429 / 503 / network failures.
 */
import { buildUrl, toApiError } from './client-http'
import { ApiError } from './errors'
import {
  isSuccessEnvelope,
  unwrap,
  unwrapItems,
  unwrapPage,
  type Page,
  type Unwrapped,
} from './envelope'
import type { RefreshOutcome } from '@/auth/refresh'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

import type { QueryValue } from './client-http'
export type { QueryValue } from './client-http'

export interface RequestOptions {
  readonly method?: HttpMethod
  readonly body?: unknown
  readonly query?: Readonly<Record<string, QueryValue>>
  readonly headers?: Readonly<Record<string, string>>
  /** Spec §9: required on booking mutations. Generate ONE per user intent and reuse it on retries. */
  readonly idempotencyKey?: string
  /** Default true. False for public routes (login, action links, branding resolve). */
  readonly auth?: boolean
  /** Override the default retry policy (e.g. disable for a countdown-sensitive call). */
  readonly retry?: Partial<RetryPolicy>
  readonly signal?: AbortSignal
  readonly timeoutMs?: number
}

export interface RetryPolicy {
  /** 429 — the limiter rejects BEFORE the handler runs, so any method is safe to retry. */
  readonly rateLimited: { readonly attempts: number; readonly maxWaitMs: number }
  /** 503 DATABASE_AT_CAPACITY — retried for GET, or for any request carrying an Idempotency-Key. */
  readonly capacity: { readonly attempts: number; readonly baseDelayMs: number }
  /** fetch() threw (offline, DNS). GET only. */
  readonly network: { readonly attempts: number; readonly delayMs: number }
}

export const DEFAULT_RETRY: RetryPolicy = {
  rateLimited: { attempts: 2, maxWaitMs: 15_000 },
  capacity: { attempts: 3, baseDelayMs: 500 },
  network: { attempts: 1, delayMs: 500 },
}

export interface ClientDeps {
  readonly apiBase: string
  readonly fetch: typeof fetch
  readonly getAccessToken: () => string | null
  /** Single-flight; see src/auth/refresh.ts. */
  readonly refresh: () => Promise<RefreshOutcome>
  readonly sleep?: (ms: number) => Promise<void>
  readonly defaultTimeoutMs?: number
}

export interface ApiClient {
  request: <T>(path: string, options?: RequestOptions) => Promise<Unwrapped<T>>
  /** `{ data: T }` → T */
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  post: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>
  put: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>
  patch: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<T>
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  /** `{ data: T[], meta: { cursor, hasMore, pageSize } }` */
  getPage: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<Page<T>>
  /** `{ data: { items: T[] } }` */
  getItems: <T>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => Promise<readonly T[]>
  /** Non-envelope bodies (CSV export). */
  requestBlob: (path: string, options?: RequestOptions) => Promise<Blob>
  /** Like `requestBlob`, plus the filename the server chose in `Content-Disposition`, if any. */
  requestDownload: (
    path: string,
    options?: RequestOptions,
  ) => Promise<{ readonly blob: Blob; readonly filename: string | null }>
}

/** `attachment; filename="x.csv"` → `x.csv`. */
export function filenameFromDisposition(header: string | null): string | null {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/iu.exec(header ?? '')
  return match?.[1] === undefined ? null : decodeURIComponent(match[1].trim())
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

export function createApiClient(deps: ClientDeps): ApiClient {
  const sleep = deps.sleep ?? defaultSleep
  const timeoutMs = deps.defaultTimeoutMs ?? 30_000

  async function send(
    path: string,
    options: RequestOptions,
    token: string | null,
  ): Promise<Response> {
    const method = options.method ?? 'GET'
    const headers = new Headers(options.headers)
    if (!headers.has('accept')) headers.set('accept', 'application/json')
    if (options.body !== undefined) headers.set('content-type', 'application/json')
    if (token !== null) headers.set('authorization', `Bearer ${token}`)
    if (options.idempotencyKey !== undefined) headers.set('idempotency-key', options.idempotencyKey)

    const controller = new AbortController()
    const timer = setTimeout(() => {
      controller.abort(new DOMException('Request timed out', 'TimeoutError'))
    }, options.timeoutMs ?? timeoutMs)
    const onOuterAbort = (): void => {
      controller.abort(options.signal?.reason)
    }
    options.signal?.addEventListener('abort', onOuterAbort, { once: true })

    try {
      return await deps.fetch(buildUrl(deps.apiBase, path, options.query), {
        method,
        headers,
        body: options.body === undefined ? null : JSON.stringify(options.body),
        // Needed only by /auth/*, harmless everywhere else on a same-origin deployment.
        credentials: 'include',
        signal: controller.signal,
      })
    } catch (cause) {
      const aborted = cause instanceof DOMException && cause.name === 'AbortError'
      const timedOut =
        controller.signal.aborted &&
        controller.signal.reason instanceof DOMException &&
        controller.signal.reason.name === 'TimeoutError'
      throw new ApiError({
        kind: timedOut || aborted ? 'timeout' : 'network',
        status: 0,
        code: 'SERVICE_UNAVAILABLE',
        message: timedOut
          ? 'The request timed out.'
          : aborted
            ? 'The request was cancelled.'
            : 'Network error — check your connection.',
        cause,
      })
    } finally {
      clearTimeout(timer)
      options.signal?.removeEventListener('abort', onOuterAbort)
    }
  }

  /**
   * Runs the request through the retry policy and the refresh-on-401 path. Returns the raw
   * Response so both envelope and blob readers share one implementation.
   */
  async function execute(path: string, options: RequestOptions): Promise<Response> {
    const policy: RetryPolicy = { ...DEFAULT_RETRY, ...options.retry }
    const auth = options.auth ?? true
    const method = options.method ?? 'GET'
    const safeToRetryOnCapacity = method === 'GET' || options.idempotencyKey !== undefined

    let rateLimitAttempts = 0
    let capacityAttempts = 0
    let networkAttempts = 0
    let refreshed = false

    for (;;) {
      let response: Response
      try {
        response = await send(path, options, auth ? deps.getAccessToken() : null)
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.kind === 'network' &&
          method === 'GET' &&
          networkAttempts < policy.network.attempts
        ) {
          networkAttempts += 1
          await sleep(policy.network.delayMs)
          continue
        }
        throw error
      }

      if (response.ok) return response

      const error = await toApiError(response)

      // 401 on an authenticated call: the access token expired (or was revoked). Refresh exactly
      // once — through the single-flight refresher — and retry once. Never for public calls.
      if (auth && error.status === 401 && error.code === 'UNAUTHENTICATED' && !refreshed) {
        refreshed = true
        const outcome = await deps.refresh()
        if (outcome.kind === 'ok') continue
        throw error
      }

      if (error.code === 'RATE_LIMITED' && rateLimitAttempts < policy.rateLimited.attempts) {
        rateLimitAttempts += 1
        const wait = Math.min((error.retryAfterSeconds ?? 1) * 1000, policy.rateLimited.maxWaitMs)
        await sleep(wait)
        continue
      }

      if (
        error.status === 503 &&
        safeToRetryOnCapacity &&
        capacityAttempts < policy.capacity.attempts
      ) {
        capacityAttempts += 1
        await sleep(policy.capacity.baseDelayMs * 2 ** (capacityAttempts - 1))
        continue
      }

      throw error
    }
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<Unwrapped<T>> {
    const response = await execute(path, options)
    if (response.status === 204) return { data: undefined as T, meta: undefined }
    const body: unknown = await response.json().catch(() => undefined)
    if (!isSuccessEnvelope(body)) {
      throw new ApiError({
        kind: 'transport',
        status: response.status,
        code: 'INTERNAL_ERROR',
        message: 'The server returned an unexpected response shape.',
        requestId: response.headers.get('x-request-id') ?? undefined,
      })
    }
    return unwrap<T>(body)
  }

  const data = async <T>(path: string, options: RequestOptions): Promise<T> =>
    (await request<T>(path, options)).data

  return {
    request,
    get: (path, options = {}) => data(path, { ...options, method: 'GET' }),
    post: (path, body, options = {}) => data(path, { ...options, method: 'POST', body }),
    put: (path, body, options = {}) => data(path, { ...options, method: 'PUT', body }),
    patch: (path, body, options = {}) => data(path, { ...options, method: 'PATCH', body }),
    delete: (path, options = {}) => data(path, { ...options, method: 'DELETE' }),
    getPage: async (path, options = {}) => {
      const response = await execute(path, { ...options, method: 'GET' })
      return unwrapPage((await response.json()) as unknown)
    },
    getItems: async (path, options = {}) => {
      const response = await execute(path, { ...options, method: 'GET' })
      return unwrapItems((await response.json()) as unknown)
    },
    requestBlob: async (path, options = {}) => {
      const response = await execute(path, {
        ...options,
        headers: { accept: '*/*', ...options.headers },
      })
      return response.blob()
    },
    requestDownload: async (path, options = {}) => {
      const response = await execute(path, {
        ...options,
        headers: { accept: '*/*', ...options.headers },
      })
      return {
        blob: await response.blob(),
        filename: filenameFromDisposition(response.headers.get('content-disposition')),
      }
    },
  }
}
